// Section · Same engine, every pipeline.
//
// Five representative Swarm-1 agent cards (Scout · Form · Value ·
// Stats · Intel) each with a rotating pipeline badge cycling through
// the six doctrinal pipelines. The point: one agent substrate, many
// surfaces.
//
// (Debate Moderator and Consensus Ranker are deliberately not
// surfaced here — they're the synthesis layer; the per-dimension
// agents are the better illustration of "the substrate".)

import { useEffect, useState } from "react";
import { PIPELINES, STATUS_COLOR } from "./landing-data.ts";

const AGENT_NAMES: ReadonlyArray<string> = ["SCOUT", "FORM", "VALUE", "STATS", "INTEL"];

export function PlatformUnderneath(): JSX.Element {
  const [cycleIdx, setCycleIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setCycleIdx((i) => (i + 1) % PIPELINES.length), 1800);
    return () => clearInterval(t);
  }, []);
  const cur = PIPELINES[cycleIdx]!;

  return (
    <section
      className="sz-l-section"
      style={{
        padding: "160px 0",
        borderTop: "1px solid var(--border)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div className="container">
        <div
          className="sz-l-section-header"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1.6fr",
            gap: 80,
            marginBottom: 60,
          }}
        >
          <div>
            <span className="bracket">Platform · A</span>
          </div>
          <div>
            <h2
              className="serif"
              style={{
                fontSize: "clamp(40px, 5vw, 68px)",
                fontWeight: 500,
                lineHeight: 1.02,
                color: "var(--text)",
                marginBottom: 22,
                letterSpacing: "-0.01em",
              }}
            >
              One agent substrate.
              <br />
              <em style={{ color: "var(--accent)", fontStyle: "italic" }}>Six pipelines.</em>
            </h2>
            <p
              style={{
                fontSize: 17,
                color: "var(--text-muted)",
                maxWidth: 640,
                lineHeight: 1.55,
              }}
            >
              Scout gates fixtures. Agent A reads form. Agent B finds mispricing. Agent C reads the
              stats. Agent D pulls real-world intel. The same agent layer powers daily slips,
              POWERBALL, MEGA, LADDER, the 7-day Singles Chain, and Owl Hour — and beyond football,
              the same substrate runs prediction markets, crypto, and forex.
            </p>
          </div>
        </div>

        {/* Five agent cards with rotating market badge */}
        <div
          className="sz-l-platform-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 1,
            background: "var(--border)",
            border: "1px solid var(--border)",
            marginBottom: 12,
          }}
        >
          {AGENT_NAMES.map((name, i) => (
            <div
              key={name}
              style={{
                padding: "32px 24px 28px",
                background: "var(--surface)",
                position: "relative",
              }}
            >
              <div
                className="mono"
                style={{
                  fontSize: 9,
                  color: "var(--text-dim)",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                }}
              >
                Agent · 0{i + 1}
              </div>
              <div
                className="display"
                style={{
                  fontSize: 22,
                  color: "var(--text)",
                  marginTop: 10,
                  marginBottom: 22,
                  letterSpacing: "0.02em",
                }}
              >
                {name}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 10px",
                  background: "rgba(212, 168, 67, 0.06)",
                  border: "1px solid var(--accent-deep)",
                  fontFamily: "var(--mono)",
                  fontSize: 10,
                  letterSpacing: "0.1em",
                  transition: "all .4s",
                }}
              >
                <span style={{ color: STATUS_COLOR[cur.status], fontSize: 14 }}>{cur.icon}</span>
                <span
                  style={{
                    color: "var(--text)",
                    textTransform: "uppercase",
                    fontWeight: 500,
                  }}
                >
                  {cur.market}
                </span>
              </div>
              <div
                className="mono"
                style={{
                  fontSize: 9,
                  color: "var(--text-dim)",
                  letterSpacing: "0.14em",
                  marginTop: 14,
                  textTransform: "uppercase",
                }}
              >
                {cur.status === "live" ? "Live · " + cur.statusLbl : cur.statusLbl}
              </div>
            </div>
          ))}
        </div>

        <p
          className="mono"
          style={{
            fontSize: 11,
            color: "var(--text-dim)",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            textAlign: "right",
          }}
        >
          / Same agents · Different pipeline · Cycling: {cur.code}
        </p>
      </div>
    </section>
  );
}
